import { TDashboardView } from "../../interfaces";
import { ILineItemWithOrder, ISessionWithBookings } from "../../interfaces/Session";
import { useBookingSession } from "../../context/BookingSession";
import useSession from "./useSession";
import { useSession as useAuthSession } from "../../components/Session";
import { getDriverCount, getFinancialStatus, getSameSessionBookings, getStatusClass, isBookedByMember } from "./utilities";
import Notes from "./Notes";
import RaceReadyMsg from "./RaceReadyMsg";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useDriverRegistration } from "../../context/DriverRegistration";
import Name from "./Name";
import CheckInComponent from "./CheckIn";
import { Tooltip } from "@material-tailwind/react";
import { IFirebaseDraftOrder } from "../../../functions/src/interfaces/firebaseEntities";
import { ICustomer, IOrderCustomer } from "shopify-api-node";
import DragHandle from "../../components/Shared/DragHandle";
import ConditionalWrapper from "../../components/Shared/ConditionalWrapper";
import { useStaffInfo } from "../../context/Staff";
import { useRaceHostSelection } from "../../context/RaceHostSelection";
import { getDatetimeFromSku } from "../../utilities";
import { BookingDriverSeats } from "../../components/Common";
import { DateTime } from "luxon";
import StartBattleKartButton from "./StartBattleKartButton";
import { useSessionSchedule } from "../../context/SessionSchedule";
import { IRacefacerSession, IRacefacerSlot } from "../../interfaces/racefacer";

interface Props {
  dashboardView: TDashboardView;
  session: ISessionWithBookings;
  draftOrders?: IFirebaseDraftOrder[];
  isItemExchangeEnable: boolean;
  handleViewOrderClick: (booking: ILineItemWithOrder | IFirebaseDraftOrder) => void;
  handleViewRegistrationModalOpen: (booking: ILineItemWithOrder) => void;
  handleDragDrop: (from: ILineItemWithOrder, to: ISessionWithBookings) => void;
  forceExpanded?: boolean | null;
}

function Session({ session, dashboardView, draftOrders = [], isItemExchangeEnable, handleViewOrderClick, handleViewRegistrationModalOpen, handleDragDrop, forceExpanded }: Props) {
  const { track: view, date: selectedDate, sessionWiseWetherData, originalSessions, refreshSessions } = useBookingSession();
  const { permissions } = useAuthSession();
  const { bookings: registrationBookings } = useDriverRegistration();
  const { data: staffData } = useStaffInfo();
  const { getHostForSession, setHostForSession } = useRaceHostSelection();
  const { sessions: scheduleSessions } = useSessionSchedule();
  const hostName = useMemo(() => getHostForSession(String(session.id))?.hostName, [getHostForSession, session]);
  const scheduleSession = useMemo(()=>{
    const dt = getDatetimeFromSku(session.sku, view);
    if (dt) {
      return scheduleSessions.find((s) => s.start_time_key.includes(dt.toFormat("yyyy-MM-dd HH:mm")));
    }
  }, [scheduleSessions, session, view])

  const trackStaff = permissions?.roles?.includes("TRACK_STAFF");
  const hasRrmFeatureAccess = permissions?.features?.includes("RRM");
  const isComplexView = useMemo(() => dashboardView === "COMPLEX_VIEW", [dashboardView]);
  const {
    sessionTime,
    filteredBookings,
    getBookingTime,
    getPaidPriceContent,
    rrmLog,
    // grmLogs,
    getRegisteredDriversCount,
    isRefundedOrReturnedBooking
  } = useSession({
    session,
    view,
    registrationBookings,
    isComplexView
  });

  const [isHovering, setIsHovering] = useState(false);
  const totalBookings = filteredBookings.reduce((sum, booking) => sum + getDriverCount(booking), 0);
  // Initialize isSessionExpanded based on whether the session is more than 30 minutes old

  const getInitialExpandedState = () => {
    const sessionDateTime = getDatetimeFromSku(session.sku, view);
    if (totalBookings === 0) return false;
    if (!sessionDateTime?.isValid) return true;
    
    const now = DateTime.now().setZone("Australia/Sydney");
    const thirtyMinutesAgo = now.minus({ minutes: 30 });
    
    // Check if the session is on a past date or more than 30 minutes old
    const isPastDate = sessionDateTime.startOf('day') < now.startOf('day');
    const isPastTime = sessionDateTime < thirtyMinutesAgo;
    
    return !isPastDate && !isPastTime;
  }

  const [isSessionExpanded, setIsSessionExpanded] = useState(getInitialExpandedState());
  const [previousExpandedState, setPreviousExpandedState] = useState(getInitialExpandedState());

  // Update isSessionExpanded when forceExpanded changes
  useEffect(() => {
    if (forceExpanded !== null) {
      setIsSessionExpanded(!!forceExpanded);
    } else {
      setIsSessionExpanded(previousExpandedState);
    }
  }, [forceExpanded, previousExpandedState]);

  // Get race hosts for current session time
  const raceHosts = useMemo(() => {
    if (!staffData || !sessionTime) return [];
    const timeKey = sessionTime.replace(" ", "").toLowerCase();
    
    
    const hosts = ((view === "MAIN_TRACK") ? staffData.mainTrack[timeKey] : 
                   (view === "VR") ? staffData.vr[timeKey] : 
                   (view === "JUNIOR_TRACK") ? staffData.juniorTrack?.[timeKey] || [] : 
                   staffData.miniTrack[timeKey]) || [];
    
    // For junior track, includes race hosts, guest experience, and VR staff
    if (view === "JUNIOR_TRACK") {
      return hosts.filter(host => 
        host.role.toLowerCase().includes("race host") ||
        host.role.toLowerCase().includes("guest experience") ||
        host.role.toLowerCase().includes("reality")
      );
    }
    
    return hosts.filter(host => host.role.toLowerCase().includes("race host"));
  }, [staffData, sessionTime, view]);

  const handleRaceHostChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const hostName = e.target.value;
    const dt = getDatetimeFromSku(session.sku, view);
    if(dt && dt.isValid){
      setHostForSession(String(session.id), { 
        hostName, 
        sku: session.sku, 
        dateStr: dt.toFormat("dd-MM-yyyy"),
        startTime: dt.toFormat("yyyy-MM-dd HH:mm")
      });
    }
  }, [setHostForSession, session, view]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsHovering(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsHovering(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      handleDragDrop(data.extraData.booking, session);
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  }, [handleDragDrop, session]);

  const toggleSessionExpanded = () => {
    setPreviousExpandedState(isSessionExpanded);
    setIsSessionExpanded(!isSessionExpanded);
  };

  const HeaderContent = ({ text, className }: { text: string, className?: string }) => {
    return (
      <>
        <span className={`font-medium px-1 ${className ? className : ""}`}>|</span>
        <span className={`font-medium px-1 font-bold whitespace-nowrap ${className ? className : ""}`}>{text} </span>
      </>
    );
  }

  const BookingName = ({ booking, isDraftOrder }: { booking: ILineItemWithOrder, isDraftOrder?: boolean }) => {
    return (
      <td className="px-3 py-2 whitespace-nowrap font-medium text-xs leading-none text-gray-700 w-[25%]">
      <div className="flex items-center gap-1">
        <ConditionalWrapper condition={isItemExchangeEnable} Wrapper={<OrderExchangeHandleWrapper booking={booking} />}>
          <div 
            onClick={() => handleViewOrderClick(booking)}
            className="cursor-pointer hover:text-purple-600 pl-[50px]"
          >
            <Name 
              customer={booking.order.customer} 
              hasMultipleBooking={getSameSessionBookings(session, booking).length > 1} 
              refundedOrReturned={isRefundedOrReturnedBooking(booking)}
              exceedsMemberLimit={checkMemberExceedsLimit(booking.order.customer)}
            />
          </div>
        </ConditionalWrapper>
      </div>
    </td>
    );
  };

  const BookingTime = ({ time, isRefundedOrReturned, isHidden }: { time: string, isRefundedOrReturned: boolean, isHidden: boolean }) => {
    return (
      <td className="px-2 py-0.5 text-center font-medium text-xs leading-none text-gray-700 w-15%">
        <div className={`${isRefundedOrReturned ? "line-through" : ""} ${isHidden && "invisible"}`}>
          {time}
        </div>
      </td>
    );
  };

  const BookingPaidPrice = ({ booking, isRefundedOrReturned, isHidden }: { booking: ILineItemWithOrder | IFirebaseDraftOrder, isRefundedOrReturned: boolean, isHidden: boolean }) => {
    return (
      <td className="px-2 py-1.5 text-center font-medium text-xs leading-none text-gray-700 w-10%">
        <div className={`${isRefundedOrReturned ? "line-through" : ""} ${isHidden && "invisible"}`}>
          {getPaidPriceContent(booking)}
        </div>
      </td>
    );
  };

  const BookingNoOfDrivers = ({ numberOfDrivers }: { numberOfDrivers: number }) => {
    return (
      <td className="px-2 py-1.5 text-center font-medium text-xs leading-none text-gray-700 w-[5%] text-[15px]">
        <div className="flex items-center justify-center">
          <div>{numberOfDrivers}</div>
        </div>
      </td>
    );
  };

  const ViewOrderButton = ({ booking }: { booking: ILineItemWithOrder | IFirebaseDraftOrder }) => {
    return (
      <button
        onClick={() => handleViewOrderClick(booking)}
        className="items-center px-2.5 py-1 border border-transparent text-xs font-bold shadow-sm text-white focus:outline-none w-max justify-center bg-[#d32efe] hover:bg-[#b217dd] rounded-full"
      >
        View Order
      </button>
    );
  };

  const BookingRegistrationStatus = ({ booking }: { booking: ILineItemWithOrder }) => {
    const ViewRegistrationButton = () => {
      return (
        <button
          onClick={() => handleViewRegistrationModalOpen(booking)}
          className={`items-center px-2.5 py-1 border border-transparent text-xs font-bold shadow-sm text-white focus:outline-none w-max justify-center rounded-full ${
            getRegisteredDriversCount(booking) === 0
              ? "bg-black"
              : getRegisteredDriversCount(booking) >= getDriverCount(booking)
              ? "bg-[#FF00FF]"
              : "bg-sky-500 hover:bg-sky-700"
          }`}
        >
          {getRegisteredDriversCount(booking)}
        </button>
      );
    };

    return (
      <td className="px-2 py-0 font-bold text-xs leading-none text-gray-700">
        <div className="flex justify-center items-center gap-2 py-0.5">
          <div className={getStatusClass(booking.order.financial_status)}>
            {getFinancialStatus(booking.order)}
          </div>
          
          <CheckInComponent 
            booking={booking} 
            selectedDate={selectedDate} 
            getDriverCount={getDriverCount} 
          />
          <ViewOrderButton booking={booking} />
          <ViewRegistrationButton />
        </div>
      </td>
    );
  };

  const HeaderInfo = ({ scheduleSession, isComplexView }: { scheduleSession: IRacefacerSession | IRacefacerSlot | undefined, isComplexView: boolean }) => {
    const Bubble = ({ color, text }: { color: string, text: string }) => {
      return (
        <div className="rounded-[10px] mx-1 px-1 py-0.5 flex items-center justify-center" style={{ backgroundColor: color }}>
          <span className="text-white font-bold text-[16px] font-medium">{text}</span>
        </div>
      );
    }

    const Price = () => {
      const getPriceColour = (price: number) => {
        if (price <= 19) return "#40BFFF";
        if (price <= 29) return "#8A75FF";
        if (price <= 39) return "#C03FFF";
        if (price <= 49) return "#E632E5";
        return "#510EA8";
      }

      return <Bubble color={getPriceColour(parseInt(session.price))} text={`$${parseInt(session.price)}`} />
    };

    const SeatsLeft = () => {
      return <HeaderContent text={`${session.inventory_quantity} left`} />
    }

    const AgeGroup = () => {
      const ageGroup = session.option1;
      if (!ageGroup) return null;

      // Remove everything before and including "Ages" from age group
      const ageGroupWithoutAges = ageGroup.replace(/^.*Ages\s*/, '');
      const ageGroupWithoutUnder = ageGroupWithoutAges.replace(/under /i, "<");

      return <HeaderContent text={ageGroupWithoutUnder} />
    }

    const StandardExpert = () => {
      const category = session.option2;
      if (category === "Standard") return null;

      return <HeaderContent text={category || ""} />
    }

    const DriverRaced = () => {
      if(!scheduleSession || scheduleSession.type !== 'session' || isNaN(scheduleSession.total_runs_count)){
        return <></>;
      }
      return <HeaderContent text={`${scheduleSession.total_runs_count} ${scheduleSession.status === "finished" ? "Raced" : "Added"}`} />
    }

    return <div className="flex items-center">
      <Price />
      {view === "MAIN_TRACK" && (
        <>
          <StandardExpert />
          <AgeGroup />
        </>
      )}
      <SeatsLeft />
      {isComplexView && <DriverRaced />}
    </div>
  }

  // Check if a session time is before midday
  const isSessionTimeBeforeMidday = useCallback((sessionTime: string | undefined): boolean => {
    if (!sessionTime) return false;
    
    const timeFormat = sessionTime.slice(-2).toLowerCase(); // 'am' or 'pm'
    const [hour] = sessionTime.slice(0, -2).split(":");
    
    // If it's PM and not 12PM, it's after midday
    if (timeFormat === 'pm' && hour !== '12') return false;
    
    // If it's AM and not 12AM, it's before midday
    if (timeFormat === 'am' && hour !== '12') return true;
    
    // For 12PM (noon), it's after midday
    if (timeFormat === 'pm' && hour === '12') return false;
    
    // For 12AM (midnight), it's before midday
    return true;
  }, []);

  // Check if member has exceeded booking limit
  const checkMemberExceedsLimit = useCallback((customer?: ICustomer | IOrderCustomer) => {
    if (!customer || !isBookedByMember(customer)) return false;
    
    // Count member bookings in all sessions
    let beforeNoonCount = 0;
    let afterNoonCount = 0;
    
    originalSessions.forEach(s => {
      const sessionTime = (["MAIN_TRACK", "JUNIOR_TRACK", "INTERMEDIATE_TRACK"].includes(view)) 
        ? s.option3?.trim()?.toLowerCase() 
        : s.option1?.trim()?.toLowerCase();
      
      const isBeforeMidday = isSessionTimeBeforeMidday(sessionTime);
      
      s.bookings.forEach(b => {
        if (b.order.customer?.id === customer.id && b.order.financial_status !== "refunded") {
          if (isBeforeMidday) {
            beforeNoonCount++;
          } else {
            afterNoonCount++;
          }
        }
      });
    });
    
    return beforeNoonCount > 4 || afterNoonCount > 4;
  }, [originalSessions, view, isSessionTimeBeforeMidday]);

  return (
    <div 
      className={`mb-2 rounded-lg overflow-hidden shadow-sm ${
        totalBookings === 0 
          ? session.inventory_quantity === 0 
            ? 'border-[#FFD8D8] bg-[#FFD8D8]' 
            : 'border-[#DBFFD1] bg-[#DBFFD1]'
          : 'border-gray-200 bg-white'
      } ${isItemExchangeEnable && isHovering ? 'outline outline-2 outline-pink-400 bg-pink-50' : ''}`}
      onDragOver={isItemExchangeEnable ? handleDragOver : undefined}
      onDragLeave={isItemExchangeEnable ? handleDragLeave : undefined}
      onDrop={isItemExchangeEnable ? handleDrop : undefined}
    >
      {/* Session Header */}
      <div className={`flex items-center justify-between px-3 py-1 ${
        totalBookings === 0 
          ? session.inventory_quantity === 0 
            ? 'border-[#FFD8D8] bg-[#FFD8D8]' 
            : 'border-[#DBFFD1] bg-[#DBFFD1]'
          : 'border-gray-200 bg-white'
      } ${isItemExchangeEnable && isHovering ? 'bg-pink-50' : ''}`}>
        <div className={`flex items-center ${isComplexView ? 'min-w-[27%]' : 'min-w-[19%]'} flex-shrink-0`}>
          <button 
            className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={toggleSessionExpanded}
          >
            <svg 
              className={`h-5 w-5 transition-transform ${isSessionExpanded ? 'transform rotate-90' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          
          <div className="flex items-center">
            <div className="font-bold text-lg text-gray-800 mr-2 min-w-[75px]">{sessionTime}</div>
            
            {(isComplexView && sessionWiseWetherData[session.sku] && sessionWiseWetherData[session.sku].weather?.length) && (
              <div className="w-10 h-7 ml-2 overflow-hidden">
                <Tooltip content={sessionWiseWetherData[session.sku].weather[0].description}>
                  <img src={`https://openweathermap.org/img/wn/${sessionWiseWetherData[session.sku].weather[0].icon}@2x.png`} alt="weather icon" className="w-10 h-10 -mt-1"/>
                </Tooltip>
              </div>
            )}
          
            {/* Session Details*/}
            <div className="flex items-center">
              <HeaderInfo scheduleSession={scheduleSession} isComplexView={isComplexView} />
            </div>
          </div>
        </div>
        {/* Booking Driver Seats */}
        <div className="ml-5 mr-5 w-[100%]">
          <BookingDriverSeats session={session} view={view} />
        </div>
        {/* Race Host Selection*/}
        <div className="flex items-center space-x-6">
          <div className="flex items-center w-[120px]">
            <select 
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 w-full"
              value={hostName || ""}
              onChange={handleRaceHostChange}
            >
              <option value="">Race Host</option>
              {raceHosts.map((host) => (
                <option key={host.name} value={host.name}>
                  {host.name}
                </option>
              ))}
            </select>
          </div>
          
          {(!trackStaff || hasRrmFeatureAccess) && (
            <div className="flex items-center w-[85px]">
              <RaceReadyMsg 
                session={session} 
                view={view} 
                rrmLog={rrmLog} 
                filteredBookings={filteredBookings} 
              />
            </div>
          )}
          {(view === "BATTLE_KART") && <StartBattleKartButton bookings={filteredBookings} />}
        </div>
      </div>

      {/* Session Content - Bookings Table */}
      {isSessionExpanded && (
        <div className={`bg-white ${isItemExchangeEnable && isHovering ? 'bg-pink-50' : ''}`}>
          <table className="min-w-full divide-y divide-gray-200">

            <tbody className={`divide-y divide-gray-100 ${isItemExchangeEnable && isHovering ? 'bg-pink-50' : 'bg-white'}`}>
              {filteredBookings.length === 0 && draftOrders.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-2 py-2 text-center text-gray-500">
                    No bookings for this session
                  </td>
                </tr>
              )}
              
              {/* Regular Bookings */}
              {filteredBookings.map(booking => (
                <tr key={booking.id} className={`hover:bg-gray-50 ${isRefundedOrReturnedBooking(booking) ? "text-gray-400" : ""}`}>
                  <BookingName booking={booking} />
                  <Notes view={view} booking={booking} refreshSessions={refreshSessions} />
                  <BookingNoOfDrivers numberOfDrivers={getDriverCount(booking)} />
                  <BookingTime time={getBookingTime(booking.order.created_at)} isRefundedOrReturned={isRefundedOrReturnedBooking(booking)} isHidden={!isComplexView} />
                  <BookingPaidPrice booking={booking} isRefundedOrReturned={isRefundedOrReturnedBooking(booking)} isHidden={!isComplexView} />
                  
                  <BookingRegistrationStatus booking={booking} />
                </tr>
              ))}
              
              {/* Draft Orders */}
              {isComplexView && draftOrders.map(draftOrder => (
                <tr key={draftOrder.id} className="text-[#d32efe] hover:bg-gray-50">
                  {/* Booking Name */}
                  <td className="px-3 py-0.5 whitespace-nowrap font-medium text-xs leading-none w-[25%]">
                    <Name 
                      className="text-[#d32efe]" 
                      customer={draftOrder.customer as ICustomer} 
                      hasMultipleBooking={false} 
                      refundedOrReturned={false}
                    />
                  </td>
                  
                  <Notes view={view} booking={draftOrder} refreshSessions={refreshSessions} />
                  
                  <BookingTime time={getBookingTime(draftOrder.created_at)} isRefundedOrReturned={false} isHidden={!isComplexView} />
                  <BookingPaidPrice booking={draftOrder} isRefundedOrReturned={true} isHidden={!isComplexView} />
                  
                  <BookingNoOfDrivers numberOfDrivers={draftOrder.line_items.find(li => li.variant_id === session.id)?.quantity || 0} />
                  <ViewOrderButton booking={draftOrder} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface OrderExchangeHandleWrapperProps {
  children?: React.ReactNode;
  booking: ILineItemWithOrder;
}

const OrderExchangeHandleWrapper: React.FC<OrderExchangeHandleWrapperProps> = ({ children, booking }) => {
  return (
    <DragHandle id={booking.id} extraData={{ booking }}>
      {children}
    </DragHandle>
  )
}

export default Session;